// src/components/CustomerNegotiationsTab.tsx - SCRIPT COMPLET CORRIGÉ
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import negotiationService, { PriceNegotiation } from '../services/negotiationService';
import productService from '../services/productService';
import { 
  MessageCircle, 
  Clock, 
  CheckCircle, 
  X, 
  RefreshCw,
  Eye,
  ShoppingCart,
  AlertCircle,
  Package,
  TrendingDown
} from 'lucide-react';

const CustomerNegotiationsTab = () => {
  const { user } = useAuth();
  
  const [negotiations, setNegotiations] = useState<PriceNegotiation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [acceptingCounterOffer, setAcceptingCounterOffer] = useState<number | null>(null);
  
  // Cache des produits
  const [products, setProducts] = useState<{[key: number]: any}>({});

  useEffect(() => {
    if (user?.is_customer) {
      loadNegotiations();
    }
  }, [user, statusFilter]);

  const loadNegotiations = async () => {
    try {
      setLoading(true);
      setError('');
      
      const negotiationsData = await negotiationService.getCustomerNegotiations(statusFilter);
      setNegotiations(negotiationsData);
      
      // Charger les détails des produits
      const productIds = [...new Set(negotiationsData.map(n => n.product_id))];
      const productPromises = productIds.map(id => 
        productService.getProduct(id).catch(() => null)
      );
      
      const productResults = await Promise.all(productPromises);
      const productMap: {[key: number]: any} = {};
      
      productResults.forEach((product, index) => {
        if (product) {
          productMap[productIds[index]] = product;
        }
      });
      
      setProducts(productMap);
      
    } catch (error) {
      console.error('Erreur lors du chargement des négociations:', error);
      setError('Erreur lors du chargement des négociations');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptCounterOffer = async (negotiation: PriceNegotiation) => {
    try {
      setAcceptingCounterOffer(negotiation.id);
      setError('');
      setSuccess('');
      
      await negotiationService.acceptCounterOffer(negotiation.id);
      
      // Recharger les données
      await loadNegotiations();
      
      setSuccess(`Contre-offre acceptée ! Vous pouvez maintenant acheter "${products[negotiation.product_id]?.name}" au prix de ${negotiationService.formatPrice(negotiation.counter_offer_price || 0)}.`);
      
    } catch (error: any) {
      console.error('Erreur lors de l\'acceptation:', error);
      
      if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Erreur lors de l\'acceptation. Veuillez réessayer.');
      }
    } finally {
      setAcceptingCounterOffer(null);
    }
  };

  const calculateDiscount = (originalPrice: number, proposedPrice: number): number => {
    return ((originalPrice - proposedPrice) / originalPrice) * 100;
  };

  // ✅ NOUVEAU - Fonction pour calculer le prix final accepté
  const getFinalAcceptedPrice = (negotiation: PriceNegotiation): number => {
    if (negotiation.status === 'accepted') {
      // Si contre-offre existe ET est différente de la proposition, customer a accepté la contre-offre
      if (negotiation.counter_offer_price && 
          negotiation.counter_offer_price !== negotiation.proposed_price) {
        return negotiation.counter_offer_price;
      }
      // Sinon, farmer a accepté la proposition du customer
      return negotiation.proposed_price;
    }
    // Pour les autres statuts, retourner la proposition
    return negotiation.proposed_price;
  };

  const getActionButtons = (negotiation: PriceNegotiation) => {
    if (negotiation.status === 'counter_offered' && negotiation.counter_offer_price) {
      return (
        <div className="flex gap-2">
          <button
            onClick={() => handleAcceptCounterOffer(negotiation)}
            disabled={acceptingCounterOffer === negotiation.id}
            className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition duration-300 disabled:opacity-50 flex items-center gap-1"
          >
            {acceptingCounterOffer === negotiation.id ? (
              <RefreshCw className="h-3 w-3 animate-spin" />
            ) : (
              <CheckCircle className="h-3 w-3" />
            )}
            Accepter
          </button>
          <Link
            to={`/product/${negotiation.product_id}`}
            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition duration-300 flex items-center gap-1"
          >
            <MessageCircle className="h-3 w-3" />
            Négocier
          </Link>
        </div>
      );
    }
    
    if (negotiation.status === 'accepted') {
      // ✅ NOUVEAU - Vérifier si la négociation est épuisée
      const isExhausted = negotiation.times_used >= negotiation.max_usage;
      
      if (isExhausted) {
        return (
          <div className="flex gap-2">
            <span className="text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              Épuisée
            </span>
            <Link
              to={`/product/${negotiation.product_id}`}
              className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition duration-300 flex items-center gap-1"
            >
              <MessageCircle className="h-3 w-3" />
              Renégocier
            </Link>
          </div>
        );
      } else {
        return (
          <Link
            to={`/product/${negotiation.product_id}`}
            className="bg-olive text-white px-3 py-1 rounded text-sm hover:bg-terracotta transition duration-300 flex items-center gap-1"
          >
            <ShoppingCart className="h-3 w-3" />
            Acheter ({negotiation.max_usage - negotiation.times_used} restante{negotiation.max_usage - negotiation.times_used > 1 ? 's' : ''})
          </Link>
        );
      }
    }
    
    if (negotiation.status === 'pending') {
      return (
        <div className="text-sm text-yellow-600 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          En attente...
        </div>
      );
    }
    
    return (
      <Link
        to={`/product/${negotiation.product_id}`}
        className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700 transition duration-300 flex items-center gap-1"
      >
        <Eye className="h-3 w-3" />
        Voir
      </Link>
    );
  };

  if (!user?.is_customer) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header avec filtre */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold text-slate">Mes négociations</h3>
          <p className="text-gray-600 text-sm">Suivez vos propositions de prix</p>
        </div>
        <button
          onClick={loadNegotiations}
          className="bg-olive text-white px-3 py-2 rounded-md hover:bg-terracotta transition duration-300 flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Actualiser
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <span className="text-red-800 text-sm">{error}</span>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span className="text-green-800 text-sm">{success}</span>
        </div>
      )}

      {/* Filtre */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate mb-2">
              Filtrer par statut
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
            >
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="counter_offered">Contre-offre reçue</option>
              <option value="accepted">Acceptées</option>
              <option value="rejected">Refusées</option>
            </select>
          </div>
          <div className="flex items-end">
            <div className="text-sm text-gray-600">
              {negotiations.length} négociation(s) au total
            </div>
          </div>
        </div>
      </div>

      {/* Liste des négociations */}
      {loading ? (
        <div className="text-center py-8">
          <RefreshCw className="h-8 w-8 animate-spin text-olive mx-auto mb-2" />
          <p>Chargement des négociations...</p>
        </div>
      ) : negotiations.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <MessageCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate mb-2">Aucune négociation</h3>
          <p className="text-gray-600 mb-4">
            {statusFilter ? 'Aucune négociation trouvée avec ce filtre.' : 'Vous n\'avez pas encore fait de propositions de prix.'}
          </p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 bg-olive text-white px-4 py-2 rounded-md hover:bg-terracotta transition duration-300"
          >
            <Package className="h-4 w-4" />
            Découvrir les produits
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {negotiations.map((negotiation) => {
            const product = products[negotiation.product_id];
            
            return (
              <div key={negotiation.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-semibold text-slate">
                        {product?.name || `Produit #${negotiation.product_id}`}
                      </h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        negotiationService.getStatusColor(negotiation.status)
                      }`}>
                        {negotiationService.getStatusLabel(negotiation.status)}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-1">
                        <Package className="h-4 w-4" />
                        <span>{negotiation.quantity} unité(s)</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{negotiationService.formatDate(negotiation.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MessageCircle className="h-4 w-4" />
                        <span>Proposition {negotiation.customer_proposals_count}/3</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="ml-4">
                    {getActionButtons(negotiation)}
                  </div>
                </div>

                {/* Détails des prix */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-gray-50 rounded p-3">
                    <div className="text-sm text-gray-600">Prix original</div>
                    <div className="text-lg font-semibold text-slate">
                      {negotiationService.formatPrice(negotiation.original_price)}
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 rounded p-3">
                    <div className="text-sm text-blue-600">Votre proposition</div>
                    <div className="text-lg font-semibold text-blue-800">
                      {negotiationService.formatPrice(negotiation.proposed_price)}
                      <div className="text-xs text-green-600">
                        -{calculateDiscount(negotiation.original_price, negotiation.proposed_price).toFixed(1)}%
                      </div>
                    </div>
                  </div>
                  
                  {negotiation.counter_offer_price && (
                    <div className="bg-green-50 rounded p-3">
                      <div className="text-sm text-green-600">Contre-offre reçue</div>
                      <div className="text-lg font-semibold text-green-800">
                        {negotiationService.formatPrice(negotiation.counter_offer_price)}
                        <div className="text-xs text-green-600">
                          -{calculateDiscount(negotiation.original_price, negotiation.counter_offer_price).toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* ✅ NOUVEAU - Affichage statut d'usage pour négociations acceptées */}
                {negotiation.status === 'accepted' && (
                  <div className={`p-3 rounded-lg mb-4 ${
                    negotiation.times_used >= negotiation.max_usage 
                      ? 'bg-red-50 border border-red-200' 
                      : 'bg-green-50 border border-green-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      {negotiation.times_used >= negotiation.max_usage ? (
                        <>
                          <AlertCircle className="h-5 w-5 text-red-600" />
                          <span className="font-medium text-red-900">Négociation épuisée</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          <span className="font-medium text-green-900">Négociation utilisable</span>
                        </>
                      )}
                    </div>
                    <div className="text-sm">
                      <span className={negotiation.times_used >= negotiation.max_usage ? 'text-red-800' : 'text-green-800'}>
                        Utilisée {negotiation.times_used}/{negotiation.max_usage} fois
                        {negotiation.times_used < negotiation.max_usage && (
                          <span className="font-medium"> - {negotiation.max_usage - negotiation.times_used} utilisation(s) restante(s)</span>
                        )}
                      </span>
                    </div>
                    {negotiation.times_used >= negotiation.max_usage && (
                      <div className="text-sm text-red-700 mt-1">
                        💡 Vous devez renégocier le prix pour ce produit si vous souhaitez bénéficier d'une réduction.
                      </div>
                    )}
                  </div>
                )}

                {/* ✅ MODIFIÉ - Total avec prix final accepté */}
                <div className="bg-terracotta bg-opacity-10 rounded p-3 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate font-medium">
                      {negotiation.status === 'accepted' 
                        ? `Prix final accepté (${negotiation.quantity} unité${negotiation.quantity > 1 ? 's' : ''})`
                        : `Total proposé (${negotiation.quantity} unité${negotiation.quantity > 1 ? 's' : ''})`
                      }
                    </span>
                    <span className="text-xl font-bold text-terracotta">
                      {negotiationService.formatPrice(getFinalAcceptedPrice(negotiation) * negotiation.quantity)}
                    </span>
                  </div>
                  {/* ✅ NOUVEAU - Afficher l'économie si négociation acceptée */}
                  {negotiation.status === 'accepted' && (
                    <div className="text-sm text-green-600 mt-1">
                      Économie: {negotiationService.formatPrice((negotiation.original_price - getFinalAcceptedPrice(negotiation)) * negotiation.quantity)}
                    </div>
                  )}
                </div>

                {/* ✅ MODIFIÉ - Statut spécifique avec prix final */}
                <div className="text-sm text-gray-600">
                  {negotiation.status === 'pending' && (
                    <span className="text-yellow-600">
                      En attente de réponse du producteur
                    </span>
                  )}
                  {negotiation.status === 'accepted' && (
                    <span className="text-green-600">
                      Négociation réussie ! Prix final accepté : {negotiationService.formatPrice(getFinalAcceptedPrice(negotiation))} par unité.
                      {getFinalAcceptedPrice(negotiation) === negotiation.counter_offer_price 
                        ? " Vous avez accepté la contre-offre du producteur."
                        : " Le producteur a accepté votre proposition."
                      }
                      {/* ✅ NOUVEAU - Indication si épuisée */}
                      {negotiation.times_used >= negotiation.max_usage && (
                        <span className="text-red-600 font-medium block mt-1">
                          ⚠️ Cette négociation a été entièrement utilisée. Renégociez pour obtenir à nouveau ce prix.
                        </span>
                      )}
                    </span>
                  )}
                  {negotiation.status === 'rejected' && (
                    <span className="text-red-600">
                      {negotiation.customer_proposals_count >= 3 
                        ? 'Limite atteinte. Prix final: ' + negotiationService.formatPrice(negotiation.original_price)
                        : `Refusée. ${3 - negotiation.customer_proposals_count} proposition(s) restante(s).`}
                    </span>
                  )}
                  {negotiation.status === 'counter_offered' && (
                    <span className="text-blue-600">
                      Le producteur vous a fait une contre-offre. Vous pouvez l'accepter ou continuer à négocier.
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomerNegotiationsTab;